/**
 * Composite two-field duration input — "‹ [amount] min. [amount] seg. ›"
 * — mirrors how durations are naturally spoken ("an hour and a half"),
 * rather than a single amount+unit pair. The minor unit is always
 * whichever TIME_UNITS entry is one step below the major unit (hores ->
 * minuts, dies -> hores, ...) and has no selector of its own; it's
 * hidden entirely when the major unit is already the smallest (segons),
 * since there's nothing smaller to break a remainder into.
 *
 * The major unit is picked with the two flanking arrow buttons — the
 * same round ‹/› buttons the image pool uses to cycle images
 * (ImagePool.ts's buildIconButton) — rather than a dropdown. "‹" moves
 * to a bigger unit pair (min./seg. -> h./min. -> dies/h. -> ...), "›" to
 * a smaller one, disabled at the ends of the scale — minuts/segons is as
 * small as it goes (segons is never offered as the *major* unit, only
 * ever as minuts' minor), mesos/setmanes as big. The row spans the full
 * width of its container with the arrows flush to
 * each edge and the four fields/labels between them evenly spaced
 * (space-between), rather than tightly packed on one side.
 *
 * Changing scale with the arrows never changes the actual duration —
 * only how much of it is visible. The real value (`totalSeconds` below)
 * is kept around separately from whatever the two fields currently show;
 * an arrow click just redisplays that same total split at the new unit
 * pair (splitDurationAtUnit()), which can make a sub-remainder in a
 * smaller unit "implicit" — e.g. 2min 30sec scaled up to h./min. shows
 * "0h 2min" (the 30s aren't representable in minuts), but scaling back
 * down to min./seg. reproduces "2min 30sec" exactly, since the 30s were
 * never actually discarded. Editing a field directly is different: that
 * commits exactly what's typed, discarding any such hidden remainder,
 * since the user just explicitly set the number.
 */
import { TIME_UNITS } from '../../time_units.js';
import { secondsToComposite, splitDurationAtUnit } from '../textFormatting.js';

export interface DurationInputOptions {
  inputClassName: string;
  arrowClassName?: string;
  labelClassName?: string;
  /** When true, a value that computes to exactly 0 commits (and
   *  displays) as the real number 0, distinct from "nothing set" —
   *  only the explicit clear() ever produces `undefined` then. Defaults
   *  to false/unset: 0 and unset are the same thing, which is what
   *  every existing caller wants (an "instant"/no-duration step has no
   *  reason to distinguish the two). */
  zeroIsValid?: boolean;
  /** Shown, grayed out via the native placeholder styling (not a real,
   *  committed value), in both fields whenever there's no real value to
   *  show — i.e. whatever this input would effectively default to while
   *  unset. Kept live via the handle's own setPlaceholderSeconds() for
   *  when that default can change after the fact (e.g. a paired
   *  duration field being edited). */
  placeholderSeconds?: number;
}

export interface DurationInputHandle {
  /** Clear the duration — resets both fields and fires onChange(undefined). */
  clear(): void;
  /** Update the displayed value WITHOUT firing onChange — for when some
   *  OTHER control (a paired field's own cross-clamping) needs this
   *  input to reflect a value it didn't itself just commit. */
  setValue(seconds: number | undefined): void;
  /** Update what's shown (grayed out) while this input has no real
   *  value of its own — see DurationInputOptions.placeholderSeconds. */
  setPlaceholderSeconds(seconds: number | undefined): void;
}

const DEFAULT_LABEL_CLASS = 'text-sm text-[#a0a0a0] font-mono';

// The smallest major unit the arrows will scale down to — minuts, not
// segons: segons only ever shows up as minuts' minor unit.
const MIN_MAJOR_IDX = TIME_UNITS.findIndex(u => u.key === 'minutes');

// Same round icon button as ImagePool.ts's buildIconButton() — the
// image pool's own prev/next cycling arrows.
const DEFAULT_ARROW_CLASS =
  'w-8 h-8 flex items-center justify-center rounded-full border border-[var(--accent-alpha-40)] ' +
  'text-[var(--accent)] bg-transparent hover:bg-[var(--accent-alpha-13)] focus:outline-none ' +
  'focus:ring-2 focus:ring-[var(--accent)] font-mono text-lg flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed';

export function renderDurationInput(
  container: HTMLElement,
  initialSeconds: number | undefined,
  onChange: (seconds: number | undefined) => void,
  options: DurationInputOptions,
): DurationInputHandle {
  const row = document.createElement('div');
  row.style.cssText = 'display: flex; justify-content: space-between; align-items: center; width: 100%;';
  container.appendChild(row);

  // The real value — survives arrow clicks untouched; only a direct field
  // edit (commitFromFields) ever changes it. See this file's doc comment.
  let totalSeconds = initialSeconds;
  // What to show (grayed out) while totalSeconds has no real value —
  // see DurationInputOptions.placeholderSeconds.
  let placeholderSeconds = options.placeholderSeconds;
  const initial = totalSeconds != null && (totalSeconds || options.zeroIsValid) ? secondsToComposite(totalSeconds) : null;
  let majorIdx = Math.max(MIN_MAJOR_IDX, TIME_UNITS.findIndex(u => u.key === (initial?.majorUnit.key ?? 'minutes')));

  const leftBtn = document.createElement('button');
  leftBtn.type = 'button';
  leftBtn.textContent = '‹';
  leftBtn.title = 'Unitats més grans';
  leftBtn.className = options.arrowClassName ?? DEFAULT_ARROW_CLASS;
  row.appendChild(leftBtn);

  // Each amount+label pair is grouped into its own flex child so
  // space-between only spreads space *between* the two pairs (and the
  // arrows) — not between an amount and its own unit label, which must
  // stay visually glued together.
  const majorGroup = document.createElement('div');
  majorGroup.style.cssText = 'display: flex; align-items: center; gap: 6px;';
  row.appendChild(majorGroup);

  const majorAmountInput = document.createElement('input');
  majorAmountInput.type = 'number';
  majorAmountInput.min = '0';
  majorAmountInput.placeholder = '0';
  majorAmountInput.className = options.inputClassName;
  majorGroup.appendChild(majorAmountInput);

  const majorUnitLabel = document.createElement('span');
  majorUnitLabel.className = options.labelClassName ?? DEFAULT_LABEL_CLASS;
  majorGroup.appendChild(majorUnitLabel);

  const minorGroup = document.createElement('div');
  minorGroup.style.cssText = 'display: flex; align-items: center; gap: 6px;';
  row.appendChild(minorGroup);

  const minorAmountInput = document.createElement('input');
  minorAmountInput.type = 'number';
  minorAmountInput.min = '0';
  minorAmountInput.placeholder = '0';
  minorAmountInput.className = options.inputClassName;
  minorGroup.appendChild(minorAmountInput);

  const minorUnitLabel = document.createElement('span');
  minorUnitLabel.className = options.labelClassName ?? DEFAULT_LABEL_CLASS;
  minorGroup.appendChild(minorUnitLabel);

  const rightBtn = document.createElement('button');
  rightBtn.type = 'button';
  rightBtn.textContent = '›';
  rightBtn.title = 'Unitats més petites';
  rightBtn.className = options.arrowClassName ?? DEFAULT_ARROW_CLASS;
  row.appendChild(rightBtn);

  const currentUnits = () => {
    const majorUnit = TIME_UNITS[majorIdx];
    const minorUnit = majorIdx > 0 ? TIME_UNITS[majorIdx - 1] : null;
    return { majorUnit, minorUnit };
  };

  // Redraw the two fields/labels from `totalSeconds`, split at whichever
  // unit pair `majorIdx` currently points to — called after an arrow
  // click (scale changed, value didn't) and once up front to populate
  // the initial state.
  const refreshDisplay = () => {
    const { majorUnit, minorUnit } = currentUnits();
    majorUnitLabel.textContent = majorUnit.abbreviation;
    if (minorUnit) {
      minorGroup.style.display = '';
      minorUnitLabel.textContent = minorUnit.abbreviation;
    } else {
      minorGroup.style.display = 'none';
    }
    leftBtn.disabled = majorIdx >= TIME_UNITS.length - 1;
    rightBtn.disabled = majorIdx <= MIN_MAJOR_IDX;

    if (totalSeconds != null && (totalSeconds || options.zeroIsValid)) {
      const { majorAmount, minorAmount } = splitDurationAtUnit(totalSeconds, majorUnit, minorUnit);
      majorAmountInput.value = String(majorAmount);
      minorAmountInput.value = minorUnit ? String(minorAmount) : '';
    } else {
      majorAmountInput.value = '';
      minorAmountInput.value = '';
      if (placeholderSeconds) {
        const { majorAmount, minorAmount } = splitDurationAtUnit(placeholderSeconds, majorUnit, minorUnit);
        majorAmountInput.placeholder = String(majorAmount);
        minorAmountInput.placeholder = minorUnit ? String(minorAmount) : '0';
      } else {
        majorAmountInput.placeholder = '0';
        minorAmountInput.placeholder = '0';
      }
    }
  };
  refreshDisplay();

  // Recompute `totalSeconds` from exactly what's typed in the two fields
  // right now, discarding whatever the previous value's hidden remainder
  // (if any) was — a direct edit always wins over scale-preservation.
  const commitFromFields = () => {
    const { majorUnit, minorUnit } = currentUnits();
    const majorRaw = majorAmountInput.value.trim();
    const majorAmount = majorRaw === '' ? 0 : parseFloat(majorRaw);
    const minorRaw = minorAmountInput.value.trim();
    const minorAmount = minorUnit && minorRaw !== '' ? parseFloat(minorRaw) : 0;

    const next = (isNaN(majorAmount) ? 0 : majorAmount) * majorUnit.seconds
      + (minorUnit && !isNaN(minorAmount) ? minorAmount * minorUnit.seconds : 0);
    totalSeconds = next > 0 || options.zeroIsValid ? next : undefined;
    onChange(totalSeconds);
  };

  // 'input' (not just 'blur'): the arrow buttons need the latest typed
  // value applied *before* their own click fires, not after — relying on
  // blur-before-click ordering alone left a window where a value typed
  // and then immediately scaled with an arrow was lost. Committing on
  // every keystroke closes that window entirely; 'blur' stays too, as a
  // harmless no-op safety net for anything that only fires on blur.
  majorAmountInput.addEventListener('input', commitFromFields);
  minorAmountInput.addEventListener('input', commitFromFields);
  majorAmountInput.addEventListener('blur', commitFromFields);
  minorAmountInput.addEventListener('blur', commitFromFields);
  leftBtn.addEventListener('click', () => {
    if (majorIdx < TIME_UNITS.length - 1) majorIdx += 1;
    refreshDisplay();
  });
  rightBtn.addEventListener('click', () => {
    if (majorIdx > MIN_MAJOR_IDX) majorIdx -= 1;
    refreshDisplay();
  });

  return {
    clear: () => {
      totalSeconds = undefined;
      refreshDisplay();
      onChange(undefined);
    },
    setValue: (seconds) => {
      totalSeconds = seconds;
      refreshDisplay();
    },
    setPlaceholderSeconds: (seconds) => {
      placeholderSeconds = seconds;
      refreshDisplay();
    },
  };
}
